import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import _t from 'counterpart';

import { CCard, CCardBody, CCol, CForm, CFormGroup, CLabel, CInput, CRow, CSwitch } from '@coreui/react';
import Select from 'react-select';
import { components } from 'react-select';
import PageLayout from '../../../components/PageLayout';
import Loading from '../../../components/Loading';
import ButtonWithLoader from '../../../components/ButtonWithLoader';
import CustomSelect from '../../../components/CustomSelect';
import InputHeader from '../../../components/InputHeader';
import ToggleSelect, { ISelectOption } from '../../../components/ToggleSelect';
import {
  getDiscountCode,
  createDiscountCode,
  updateDiscountCode,
  getPropFunnels,
} from '../../../services/BackendService';
import { ICreateDiscountCodeFormData, IUpdateDiscountCodeFormData, IDiscountCode, IDiscountCodeFunnel } from './types';
import { IPropFunnel } from '../propFunnels/types';
import { extractErrorMessage, findErrorFromValidation } from '../../../helpers';
import { useAppSelector } from '../../../helpers/customHooks';
import { Permission } from '../../../reducers/userReducer';
import toast from 'react-hot-toast';
import Error from '../../../components/Error';

const discountTypeOptions = [
  {
    label: 'Fixed Amount',
    value: 'fixed',
  },
  {
    label: 'Percentage',
    value: 'percentage',
  },
];

const DiscountCodeFormPage = () => {
  const [formData, setFormData] = useState<ICreateDiscountCodeFormData>({
    name: '',
    code: '',
    fixedDiscountAmount: undefined,
    percentageDiscount: undefined,
    startDate: undefined,
    endDate: undefined,
    maxUsesPerUser: undefined,
    isActive: true,
    funnelIds: [],
  });

  const [discountType, setDiscountType] = useState<'fixed' | 'percentage'>('fixed');

  const permissions = useAppSelector((state) => state.user.permissions);
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();

  const isEditMode = new URLSearchParams(location.search).get('edit') !== null;
  const editId = new URLSearchParams(location.search).get('edit');

  const { data: propFunnels, isLoading: propFunnelsLoading } = useQuery<IPropFunnel[]>(
    'prop-funnels',
    () => getPropFunnels(),
    {
      onError: (e: any) => {
        const error = extractErrorMessage(e);
        toast.error(error);
      },
    }
  );

  const { data: existingDiscountCode, isLoading: existingDiscountCodeLoading } = useQuery<IDiscountCode>(
    ['discount-code', editId],
    () => getDiscountCode(editId!),
    {
      enabled: isEditMode && !!editId,
      onError: (e: any) => {
        const error = extractErrorMessage(e);
        toast.error(error);
      },
      retry: false,
    }
  );

  const findError = (paramName: string) => {
    return findErrorFromValidation(createDiscountCodeMutation.error || updateDiscountCodeMutation.error, paramName);
  };

  const createDiscountCodeMutation = useMutation((data: ICreateDiscountCodeFormData) => createDiscountCode(data), {
    onSuccess: async () => {
      await queryClient.invalidateQueries('discount-codes');
      toast.success(_t('discount-codes.discount-code-created-successfully'));
      history.push('/discount-codes');
    },
    onError: (e: any) => {
      const error = extractErrorMessage(e);
      toast.error(error);
    },
    retry: false,
  });

  const updateDiscountCodeMutation = useMutation(
    (data: IUpdateDiscountCodeFormData) => updateDiscountCode(editId!, data),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries('discount-codes');
        toast.success(_t('discount-codes.discount-code-updated-successfully'));
        history.push('/discount-codes');
      },
      onError: (e: any) => {
        const error = extractErrorMessage(e);
        toast.error(error);
      },
      retry: false,
    }
  );

  useEffect(() => {
    if (existingDiscountCode && isEditMode) {
      setFormData({
        name: existingDiscountCode.name,
        code: existingDiscountCode.code,
        fixedDiscountAmount: existingDiscountCode.fixedDiscountAmount
          ? Number(existingDiscountCode.fixedDiscountAmount)
          : undefined,
        percentageDiscount: existingDiscountCode.percentageDiscount
          ? Number(existingDiscountCode.percentageDiscount)
          : undefined,
        startDate: existingDiscountCode.startDate ? new Date(existingDiscountCode.startDate) : undefined,
        endDate: existingDiscountCode.endDate ? new Date(existingDiscountCode.endDate) : undefined,
        maxUsesPerUser: existingDiscountCode.maxUsesPerUser || undefined,
        isActive: existingDiscountCode.isActive,
        funnelIds: existingDiscountCode.funnels.map((f: IDiscountCodeFunnel) => f.funnelId),
      });

      if (existingDiscountCode.fixedDiscountAmount) {
        setDiscountType('fixed');
      } else if (existingDiscountCode.percentageDiscount) {
        setDiscountType('percentage');
      }
    }
  }, [existingDiscountCode, isEditMode]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDiscountTypeChange = (type: 'fixed' | 'percentage') => {
    setDiscountType(type);
    setFormData((prev) => ({
      ...prev,
      fixedDiscountAmount: type === 'fixed' ? prev.fixedDiscountAmount : undefined,
      percentageDiscount: type === 'percentage' ? prev.percentageDiscount : undefined,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isEditMode) {
      const { code, ...updateData } = formData;
      updateDiscountCodeMutation.mutate(updateData);
    } else {
      const submitData = {
        ...formData,
        code: formData.code.toUpperCase(),
      };
      createDiscountCodeMutation.mutate(submitData);
    }
  };

  const hasPermission = (permission: Permission) => {
    return permissions?.includes(permission);
  };

  if (propFunnelsLoading || (isEditMode && existingDiscountCodeLoading)) {
    return <Loading />;
  }

  if (!hasPermission(Permission.MANAGE_PROP_FUNNELS)) {
    return <Error onRetry={() => window.location.reload()} />;
  }

  const funnelOptions: ISelectOption[] =
    propFunnels?.map((funnel) => ({
      value: funnel.id,
      label: funnel.title,
      enabled: formData.funnelIds.includes(funnel.id),
    })) || [];

  return (
    <PageLayout title={isEditMode ? _t('discount-codes.edit-title') : _t('discount-codes.create-title')}>
      <CForm onSubmit={handleSubmit}>
        <CRow>
          <CCol>
            <CCard>
              <CCardBody>
                <CFormGroup>
                  <CLabel htmlFor="name">{_t('global.name')} *</CLabel>
                  <CInput
                    id="name"
                    value={formData.name}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('name', e.target.value)}
                    invalid={!!findError('name')}
                  />
                  {findError('name') && <div className="text-danger">{findError('name')}</div>}
                </CFormGroup>

                <CFormGroup>
                  <CLabel htmlFor="code">
                    {_t('discount-codes.code')} {!isEditMode && '*'}
                  </CLabel>
                  <CInput
                    id="code"
                    value={formData.code}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleInputChange('code', e.target.value.toUpperCase())
                    }
                    invalid={!!findError('code')}
                    placeholder="e.g., SAVE20"
                    disabled={isEditMode}
                  />
                  <div className="text-muted small">
                    {isEditMode ? _t('discount-codes.code-cannot-be-changed') : _t('discount-codes.code-help')}
                  </div>
                  {findError('code') && <div className="text-danger">{findError('code')}</div>}
                </CFormGroup>

                <CFormGroup className="mb-3">
                  <InputHeader
                    labelText={_t('discount-codes.discount-type')}
                    resetFilter={!isEditMode ? () => handleDiscountTypeChange('fixed') : undefined}
                  />
                  <CustomSelect
                    isDisabled={isEditMode}
                    className="w-100"
                    options={discountTypeOptions}
                    name="discountType"
                    value={discountTypeOptions.find((opt) => opt.value === discountType) ?? ''}
                    whiteBackground
                    onChange={(newValue: any) => handleDiscountTypeChange(newValue.value)}
                    fullWidth={true}
                  />
                  {findError('discountType') && <div className="text-danger">{findError('discountType')}</div>}
                </CFormGroup>

                <CFormGroup>
                  {discountType === 'fixed' ? (
                    <>
                      <CLabel htmlFor="fixedDiscountAmount">{_t('discount-codes.fixed-amount')} (USD) *</CLabel>
                      <CInput
                        id="fixedDiscountAmount"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.fixedDiscountAmount || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleInputChange('fixedDiscountAmount', parseFloat(e.target.value) || undefined)
                        }
                        invalid={!!findError('fixedDiscountAmount')}
                      />
                      {findError('fixedDiscountAmount') && (
                        <div className="text-danger">{findError('fixedDiscountAmount')}</div>
                      )}
                    </>
                  ) : (
                    <>
                      <CLabel htmlFor="percentageDiscount">{_t('discount-codes.percentage')} *</CLabel>
                      <CInput
                        id="percentageDiscount"
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={formData.percentageDiscount || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleInputChange('percentageDiscount', parseFloat(e.target.value) || undefined)
                        }
                        invalid={!!findError('percentageDiscount')}
                      />
                      {findError('percentageDiscount') && (
                        <div className="text-danger">{findError('percentageDiscount')}</div>
                      )}
                    </>
                  )}
                </CFormGroup>

                <CFormGroup>
                  <CLabel htmlFor="funnelIds">{_t('discount-codes.applicable-funnels')}</CLabel>
                  <ToggleSelect
                    options={funnelOptions}
                    onOptionsChanged={(options) => {
                      const selectedValues = options
                        .filter((option) => option.enabled && option.value !== '')
                        .map((option) => option.value);
                      handleInputChange('funnelIds', selectedValues);
                    }}
                    inputValue=""
                    onInputValueChanged={() => {}}
                    selectAll={false}
                    onSelectChanged={() => {}}
                    placeholder={_t('discount-codes.select-funnels')}
                    className="w-100"
                  />
                </CFormGroup>

                <CFormGroup>
                  <CLabel htmlFor="maxUsesPerUser">{_t('discount-codes.max-uses-per-user')}</CLabel>
                  <CInput
                    id="maxUsesPerUser"
                    type="number"
                    min="1"
                    value={formData.maxUsesPerUser || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleInputChange('maxUsesPerUser', parseInt(e.target.value) || undefined)
                    }
                    invalid={!!findError('maxUsesPerUser')}
                    placeholder={_t('discount-codes.unlimited')}
                  />
                  {findError('maxUsesPerUser') && <div className="text-danger">{findError('maxUsesPerUser')}</div>}
                </CFormGroup>

                <CFormGroup>
                  <CLabel>{_t('competitions.columns.start')}</CLabel>
                  <CInput
                    type="datetime-local"
                    value={formData.startDate ? new Date(formData.startDate).toISOString().slice(0, 16) : ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const date = new Date(e.target.value);
                      handleInputChange('startDate', date);
                    }}
                  />
                  {findError('startDate') && <div className="text-danger">{findError('startDate')}</div>}
                </CFormGroup>

                <CFormGroup>
                  <CLabel>{_t('competitions.columns.end')}</CLabel>
                  <CInput
                    type="datetime-local"
                    value={formData.endDate ? new Date(formData.endDate).toISOString().slice(0, 16) : ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const date = new Date(e.target.value);
                      handleInputChange('endDate', date);
                    }}
                  />
                  {findError('endDate') && <div className="text-danger">{findError('endDate')}</div>}
                </CFormGroup>

                <CFormGroup>
                  <CLabel>{_t('discount-codes.is-active')}</CLabel>
                  <br></br>
                  <CSwitch
                    size="lg"
                    shape="pill"
                    color="primary"
                    checked={formData.isActive}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleInputChange('isActive', e.target.checked)
                    }
                  />
                  <br></br>
                </CFormGroup>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
        <CRow>
          <CCol>
            <ButtonWithLoader
              isLoading={createDiscountCodeMutation.isLoading || updateDiscountCodeMutation.isLoading}
              type="submit"
              buttonColor="primary"
              spinnerColor="secondary"
              title={isEditMode ? _t('action.update') : _t('action.save')}
              className="instrument-buttons mt-2"
            />
          </CCol>
        </CRow>
      </CForm>
    </PageLayout>
  );
};

export default DiscountCodeFormPage;
